import React from 'react';
import Select from 'react-select';
import { useLayoutStore } from '../../stores/useLayoutStore';

export interface SearchableSelectOption {
  value: string | number;
  label: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value?: string | number | null;
  onChange: (value: string | number | null) => void;
  placeholder?: string;
  isClearable?: boolean;
  isDisabled?: boolean;
  className?: string;
  isLoading?: boolean;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = "Sélectionner...",
  isClearable = true,
  isDisabled = false,
  className = "",
  isLoading = false,
}) => {
  // Obtenir le thème actif depuis le store
  const layoutModeType = useLayoutStore((state) => state.layoutModeType);
  const isDarkMode = layoutModeType === 'dark';

  // Convertir la valeur en format react-select
  const selectedOption = value !== null && value !== undefined && value !== ""
    ? options.find(opt => String(opt.value) === String(value))
    : null;

  // Gérer le changement de valeur
  const handleChange = (selected: SearchableSelectOption | null) => {
    onChange(selected ? selected.value : null);
  };

  // Styles personnalisés adaptés au thème (light/dark)
  const customStyles: any = {
    control: (provided: any, state: any) => {
      const borderColor = state.isFocused 
        ? (isDarkMode ? '#86b7fe' : '#86b7fe')
        : (isDarkMode ? '#495057' : '#ced4da');
      
      return {
        ...provided,
        minHeight: '38px',
        backgroundColor: isDarkMode ? '#212529' : '#fff',
        borderColor: borderColor,
        color: isDarkMode ? '#fff' : '#212529',
        boxShadow: state.isFocused 
          ? (isDarkMode 
              ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' 
              : '0 0 0 0.25rem rgba(13, 110, 253, 0.25)')
          : 'none',
        '&:hover': {
          borderColor: borderColor,
        },
      };
    },
    singleValue: (provided: any) => ({
      ...provided,
      color: isDarkMode ? '#fff' : '#212529',
    }),
    input: (provided: any) => ({
      ...provided,
      color: isDarkMode ? '#fff' : '#212529',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: isDarkMode ? '#adb5bd' : '#6c757d',
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#212529' : '#fff',
      border: isDarkMode ? '1px solid #495057' : '1px solid #ced4da',
      zIndex: 99999,
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 99999,
    }),
    menuList: (provided: any) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#212529' : '#fff',
    }),
    option: (provided: any, state: any) => {
      let backgroundColor;
      let color;
      
      if (state.isSelected) {
        backgroundColor = '#0d6efd';
        color = '#fff';
      } else if (state.isFocused) {
        backgroundColor = isDarkMode ? '#495057' : '#e7f1ff';
        color = isDarkMode ? '#fff' : '#212529';
      } else {
        backgroundColor = isDarkMode ? '#212529' : '#fff';
        color = isDarkMode ? '#fff' : '#212529';
      }
      
      return {
        ...provided,
        backgroundColor,
        color,
        '&:active': {
          backgroundColor: state.isSelected ? '#0d6efd' : (isDarkMode ? '#495057' : '#e7f1ff'),
        },
      };
    },
    indicatorSeparator: (provided: any) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#495057' : '#ced4da',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      color: isDarkMode ? '#adb5bd' : '#6c757d',
      '&:hover': {
        color: isDarkMode ? '#fff' : '#212529',
      },
    }),
    clearIndicator: (provided: any) => ({
      ...provided,
      color: isDarkMode ? '#adb5bd' : '#6c757d',
      '&:hover': {
        color: isDarkMode ? '#fff' : '#212529',
      },
    }),
  };

  return (
    <Select<SearchableSelectOption, false>
      options={options}
      value={selectedOption}
      onChange={handleChange}
      placeholder={placeholder}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isSearchable={true}
      isMulti={false}
      styles={customStyles}
      className={className}
      classNamePrefix="react-select"
      noOptionsMessage={() => "Aucune option disponible"}
      getOptionValue={(option: SearchableSelectOption) => String(option.value)}
      getOptionLabel={(option: SearchableSelectOption) => option.label}
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
      menuPosition="fixed"
    />
  );
};

export default SearchableSelect;
